import React, { useEffect, useState } from "react";
import { PasswordInput } from "@/components/ui/password-input";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, School as SchoolIcon, Fingerprint } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { BiometricOfferDialog } from "@/components/BiometricOfferDialog";
import { bioAvailable, bioDismissed, bioFlag, loginBio } from "@/lib/biometric";

export default function LoginPage() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [canBio, setCanBio] = useState(false);
  const [offer, setOffer] = useState(null);
  useEffect(() => { bioAvailable().then(setCanBio); }, []);

  const finish = (u) => {
    setUser(u);
    toast.success(`Olá, ${u.name}!`);
    nav("/", { replace: true });
  };

  const bioLogin = async () => {
    setSaving(true);
    const r = await loginBio();
    setSaving(false);
    if (r.ok) finish(r.user); else toast.error(r.error);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post("/auth/admin-login", {
        email: form.email,
        password: form.password,
      });
      if (canBio && !bioFlag() && !bioDismissed()) { setOffer(r.data); return; }
      finish(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "E-mail ou senha inválidos");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl p-8 md:p-10 card-shadow border border-slate-100">
          <div className="flex justify-center mb-6">
            <Logo />
          </div>
          <div className="text-center mb-6">
            <h1 className="font-heading text-xl font-bold text-slate-900">Painel administrativo</h1>
            <p className="text-slate-500 mt-1 text-sm">Entre com suas credenciais</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">E-mail</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="admin-login-email"
                  required
                  className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent"
                  placeholder="amcescolas@gmail.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Senha</label>
              <div className="relative">
                <Lock className="absolute z-10 pointer-events-none left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <PasswordInput
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="admin-login-password"
                  required
                  className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#7c3aed] focus:border-transparent"
                  placeholder="Sua senha"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={saving}
              className="w-full h-11 bg-[#7c3aed] hover:bg-[#6d28d9] text-white rounded-xl font-semibold"
              data-testid="admin-login-submit"
            >
              {saving ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          {canBio && bioFlag() && (
            <button type="button" onClick={bioLogin} disabled={saving} data-testid="admin-login-biometric"
              className="mt-3 w-full h-12 rounded-xl border-2 border-[#7c3aed] text-[#7c3aed] font-bold text-sm inline-flex items-center justify-center gap-2 hover:bg-violet-50 transition-colors disabled:opacity-60">
              <Fingerprint className="w-5 h-5" /> Entrar com Face ID / digital
            </button>
          )}
          <BiometricOfferDialog open={!!offer} onDone={() => { const u = offer; setOffer(null); finish(u); }} />

          <div className="mt-6 pt-5 border-t border-slate-100">
            <p className="text-center text-xs text-slate-500 mb-3">É diretor(a) de uma escola?</p>
            <a
              href="/escola/login"
              data-testid="btn-school-portal"
              className="flex items-center justify-center gap-2 w-full h-11 rounded-xl border-2 border-[#1e3a8a] text-[#1e3a8a] font-semibold text-sm hover:bg-[#1e3a8a] hover:text-white transition-colors"
            >
              <SchoolIcon className="w-4 h-4" />
              Entrar como diretor
            </a>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          © {new Date().getFullYear()} AMC Escolas — Todos os direitos reservados
        </p>
      </div>
    </div>
  );
}
